import { Injectable, inject } from "@angular/core";
import {Actions} from "@ngrx/effects";
import {select, Store} from "@ngrx/store";
import {mergeMap, Observable, withLatestFrom} from "rxjs";
import {catchError, map} from "rxjs/operators";
import {TechInfoService} from "@tech/pages/infos/services/tech-info.service";
import {TechInfosActions} from "@tech/pages/infos/store/tech-info.actions-type";
import {ILocalPageOutputDto} from "@shared/interfaces/orga-response.interface";
import {InfoListDto, TenantInfoDto, TokenDto} from "@server-models";
import {ILocalPagination} from "@shared/interfaces/pagination.interface";
import {selectInfoPageList, selectInfoPagePagination} from "@tech/pages/infos/store/tech-info.selectors";
import {SharedToastControllerService} from "@shared/services/shared-toast-controller.service";
import {TranslateService} from "@ngx-translate/core";
import {ILocalInfosMyReadDto} from "@tech/pages/infos/interfaces/infos-my-read-dto.interface";
import {TechInfoEffectsBase} from "@tech/pages/infos/store/tech-info.effects";
import {ILocalRequestPayload} from "@shared/interfaces/local-request-payload.interface";
import {SharedLoginSelectors} from "@shared/components/login/store/shared-login.selector-type";

@Injectable({
  providedIn: 'root'
})
export class TechInfoApiEffects extends TechInfoEffectsBase {
  private _infosService = inject(TechInfoService);
  private _toastControllerService = inject(SharedToastControllerService);
  private _translateService = inject(TranslateService);


  constructor() {
    const actions$ = inject(Actions);
    const store = inject(Store);

    super(store, actions$);
  }

  /**
   * @name postMyRead
   * @memberof TechInfoApiEffects
   * @description
   * Receive the action started on TechInfosEffectsBase and use the latest state from infos.team
   * to use the userName for the toast
   * @param action$
   */
  postMyRead(action$: Observable<{ id: number }>): Observable<(ILocalPageOutputDto<ILocalInfosMyReadDto> | any)> {
    return action$.pipe(
      withLatestFrom(this.store.pipe(select(SharedLoginSelectors.selectToken))),
      withLatestFrom(this.store.pipe(select(selectInfoPageList))),
      mergeMap(([[currentId, appState], currentInfoList]) =>
        this._requestPostMyRead(currentId, this._getCurrentUserName(appState), currentInfoList)
      )
    );
  }

  /**
   * @name getInfoList
   * @memberof TechInfoApiEffects
   * @description
   * Receive the action started on TechInfosEffectsBase and use the latest state from infos.pagination
   * to check if there is more pages available and call a function to request infos data
   * @param action$
   * @return {Observable<(ILocalPageOutputDto<InfoListDto[]> | any)>}
   */
  getInfoList(action$: Observable<ILocalRequestPayload>): Observable<(ILocalPageOutputDto<InfoListDto[]> | any)> {
    return action$.pipe(
      withLatestFrom(this.store.pipe(select(selectInfoPagePagination))),
      mergeMap(([action, paginationState]) => {
          if (action.isRefresh) {
            return this._requestInfoList(action);
          } else if (this._isLastPagePaginationState(action, paginationState)) {
            return this._requestInfoList(action);
          } else {
            return [TechInfosActions.getInfoListCancel()];
          }
        }
      )
    );
  }

  /**
   * @name _isLastPagePaginationState
   * @memberof TechInfoApiEffects
   * @description
   * Check if there is not paging information or if the pageNumber in the action is
   * less than or equal to the total number of pages available in the paginationState
   * @param action
   * @param state
   * @private
   * @return {boolean}
   */
  private _isLastPagePaginationState(action: ILocalRequestPayload, state: ILocalPagination): boolean {
    return !state || (action?.pagination?.pageNumber ?? 0) <= state.totalPages
  }

  /**
   * @name _requestInfoList
   * @memberof TechInfoApiEffects
   * @description
   * Request the data to infoService and map the value to launch and pass throw the action need it
   * @param action
   * @private
   */
  private _requestInfoList(action: ILocalRequestPayload): Observable<(ILocalPageOutputDto<InfoListDto[]> | any)> {
    return this._infosService.getInfoList(action).pipe(
      map((data: ILocalPageOutputDto<InfoListDto[]>) =>
        action.isRefresh
          ? TechInfosActions.getInfoListRefresh({ data })
          : TechInfosActions.getInfoListSuccess({ data })
      ),
      catchError((error) => [TechInfosActions.getInfoListFail({ error })])
    )
  }

  /**
   * @name _requestPostMyRead
   * @memberof TechInfoApiEffects
   * @description
   * Request the data to infoService, call the toast service and launch the action need it
   * @param action
   * @param userName
   * @param currentInfoList
   * @private
   */
  private _requestPostMyRead(action: {
    id: number
  }, userName: string, currentInfoList: InfoListDto[]): Observable<(ILocalPageOutputDto<ILocalInfosMyReadDto> | any)> {
    return this._infosService.postMyReadById(action.id).pipe(
      map((data: ILocalPageOutputDto<ILocalInfosMyReadDto>) => {
        const updatedItems = this._updateReadAtItem(currentInfoList, action.id);
        this._toastControllerService.observablePresent({ message: this._formatMyReadToastMessage(userName) });
        return TechInfosActions.postItemsMyReadSuccess({ updatedItems: updatedItems, data })
      }),
      catchError((error) => [TechInfosActions.postItemsMyReadFail({ error })])
    )
  }

  /**
   * @name _getCurrentUserName
   * @memberof TechInfoApiEffects
   * @description
   * return the current name of the user
   * @param appState
   * @private
   */
  private _getCurrentUserName(appState: TokenDto | null | undefined): string {
    const currentInfoTenants = appState?.info?.tenants as TenantInfoDto[];
    return currentInfoTenants[0].displayName as string;
  }

  /**
   * @name _formatMyReadToastMessage
   * @memberof TechInfoApiEffects
   * @description
   * Format the message to send the toast
   * @param userName
   * @private
   */
  private _formatMyReadToastMessage(userName: string): string {
    const preSentence = this._translateService.instant("GENERAL.TOAST.INFOS.CARDS.READ.USER");
    const postSentence = this._translateService.instant("GENERAL.TOAST.INFOS.CARDS.READ.READY");
    return `${ preSentence } ${ userName }: ${ postSentence }.`;
  }

  /**
   * @name _updateReadAtItem
   * @description
   * find the itemId in the existing items and set a temporal date in isRead
   * to be able to filter by isRead values since the request don't return anything more than just success
   * @memberof TechInfoApiEffects
   * @param items
   * @param itemId
   * @private
   */
  private _updateReadAtItem(items: InfoListDto[], itemId: number): InfoListDto[] {
    const itemIndex = items.findIndex(item => item.infoId === itemId);

    if (itemIndex === -1) {
      return items;
    }

    const updatedItems = [...items];
    updatedItems[itemIndex] = {
      ...updatedItems[itemIndex],
      isRead: !updatedItems[itemIndex].isRead
    };
    return updatedItems;
  }
}
